import MetadataObject from "api/lib/data/interfaces/MetadataObject";
import Doubt from "api/lib/abstract_query/interfaces/Doubt";
import * as _ from "lodash";
import * as React from "react";
import { Dropdown, Icon, Segment } from "semantic-ui-react";
import {
  AceEditor,
  snippetsModule,
  updateSchemaCompleter,
} from "./ace_messages";
import DoubtType from "api/lib/abstract_query/interfaces/DoubtType";
// eslint-disable-next-line
import Firepad from "firepad";
import SearchboxCollab from "api/lib/data/SearchboxCollab";
import Version from "api/lib/data/Version";

export interface ISearchBoxProps {
  authUser: any;
  version: Version;
  metadata: MetadataObject;
  value: string;
  onChange: (str: string) => void;
  onClearQuery: () => void;
  doubts: Doubt[];
}
const editorProps = { $blockScrolling: Infinity };
const SearchBox = ({
  authUser,
  version,
  value,
  metadata,
  onChange,
  onClearQuery,
  doubts,
}: ISearchBoxProps): React.ReactElement => {
  const ref = React.useRef(null);
  const [editorRef, setEditorRef] = React.useState(null);
  const [showDoubts, setShowDoubts] = React.useState(false);

  // For some reason, value for AceEditor should be managed in this way - otherwise it goes berserk when selecting text
  const [currentValue, setCurrentValue] = React.useState(value);
  const [currentDoubts, setCurrentDoubts] = React.useState(doubts);

  React.useEffect(() => {
    if (isSortDoubt(doubts)) {
      editorRef.editor.setValue("sort by ", 1);
      editorRef.editor.focus();
    } else {
      setCurrentValue(value);
    }
    setCurrentDoubts(doubts);

    setShowDoubts(doubts && doubts.length > 0);
  }, [value, doubts]);

  const handleChange = React.useCallback(
    (str) => {
      setCurrentValue(str);
      //   onChange(str);
    },
    [value]
  );

  const isSortDoubt = (tdoubts: Doubt[]) => {
    const found = _.find(tdoubts, (doubt: Doubt) => {
      return doubt.type === DoubtType.NEED_SORTING_COLUMN;
    });
    if (found) {
      return true;
    }
    return false;
  };

  // const handleSubmit = React.useCallback(
  //   (str) => {
  //     if (str) {
  //       onChange(str);
  //       setCurrentValue("");
  //       setShowDoubts(false);
  //     }
  //   },
  //   [value]
  // );

  const editorOptions = React.useMemo(
    () => ({
      behavioursEnabled: true,
      enableSnippets: true,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      autoScrollEditorIntoView: true,
    }),
    []
  );

  React.useEffect(() => {
    if (editorRef) {
      const editorId = editorRef.editor.id;
      updateSchemaCompleter(editorId, metadata.tables);
      return () => {
        updateSchemaCompleter(editorId, null);
      };
    }
  }, [editorRef, metadata]);

  // React.useEffect(() => {
  //   function resize() {
  //     if (editorRef) {
  //       editorRef.editor.resize();
  //     }
  //   }

  //   if (container) {
  //     resize();
  //     // const unwatch = resizeObserver(container, resize);
  //     return () => {// TODO };
  //   }
  // }, [container, editorRef]);

  const initEditor = React.useCallback((editor) => {
    editor.setAutoScrollEditorIntoView(true);
    editor.setOption("maxLines", 3);
    editor.setOption("indentedSoftWrap", false);
    editor.setOption("fontSize", "18px");
    const row = editor.session.getLength() - 1;
    const column = editor.session.getLine(row).length; // or simply Infinity
    editor.gotoLine(row + 1, column);

    // Release Cmd/Ctrl+L to the browser
    editor.commands.addCommand({
      name: "return",
      bindKey: { win: "Return", mac: "Return" },
      exec: (thisEditor) => {
        const curVal = thisEditor.getValue();
        if (curVal) {
          onChange(thisEditor.getValue());
          thisEditor.setValue("", 1);
          setShowDoubts(false);
        }
      },
    });

    editor.commands.addCommand({
      name: "shiftreturn",
      bindKey: { win: "Shift+Return", mac: "Shift+Return" },
      exec: (thisEditor) => {
        const currentEValue = thisEditor.getValue();
        thisEditor.setValue(currentEValue + "\n", 1);
      },
    });

    // Reset Completer in case dot is pressed
    editor.commands.on("afterExec", (e) => {
      if (
        e.command.name === "insertstring" &&
        e.args === "." &&
        editor.completer
      ) {
        editor.completer.showPopup(editor);
      }
    });

    const snippetManager = snippetsModule.snippetManager;
    const m = {
      snippetText: "",
      snippets: "",
      scope: "sql",
    };
    m.snippets = snippetManager.parseSnippetFile(m.snippetText);
    snippetManager.register([], m.scope);
    if (version) {
      const searchboxCollab = new SearchboxCollab(version);
      const firepad = Firepad.fromACE(searchboxCollab.get(), editor, {
        defaultText: "",
      });
      firepad.setUserId(authUser.displayName.split("@")[0]); // TODO get valid displayname
    }

    editor.focus();
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      paste: (text) => {
        if (editorRef) {
          const { editor } = editorRef;
          editor.session.doc.replace(editor.selection.getRange(), text);
          const range = editor.selection.getRange();
          editor.selection.setRange(range);
        }
      },
      focus: () => {
        if (editorRef) {
          editorRef.editor.focus();
        }
      },
    }),
    [editorRef, onChange]
  );

  const displayDoubt = (doubt: Doubt) => {
    if (doubt.type === DoubtType.AMBIGUITY) {
      const clueOption = _.map(doubt.clues, (clue, index) => ({
        key: `clueIndex${index}`,
        text: clue.toLowerCase(),
        value: clue.toLowerCase(),
      }));
      const placeholderTxt = `pick a column for "${_.trim(doubt.txt)}"`;
      return (
        <div>
          <Icon name="bullhorn" style={{ fontStyle: "17px" }} />
          <Dropdown
            placeholder={placeholderTxt}
            options={clueOption}
            onChange={handleDateChanged}
            inline={true}
          />
          {/* {"  "}
          <span>
            <span style={{ fontStyle: "italic" }}>for "this quarter"</span>
          </span> */}
        </div>
      );
    }
    if (doubt.type === DoubtType.NEED_SORTING_COLUMN) {
      return (
        <span>
          <Icon name="bullhorn" style={{ fontStyle: "17px" }} />
          need a sorting column
        </span>
      );
    }
    if (doubt.type === DoubtType.NEED_DATE_COLUMN) {
      const clueOption = _.map(doubt.clues, (clue, index) => ({
        key: `clueIndex${index}`,
        text: clue.toLowerCase(),
        value: clue.toLowerCase(),
      }));

      return (
        <div>
          <Icon name="bullhorn" style={{ fontStyle: "17px" }} />
          <Dropdown
            placeholder={`pick a date for '${doubt.txt}'`}
            options={clueOption}
            onChange={handleDateChanged}
            inline={true}
          />
        </div>
      );
    }
  };

  const displayDoubts = () => {
    return (
      <div className="displayDoubts">
        {currentDoubts.map((doubt: Doubt, doubtIndex: number) => {
          return (
            <React.Fragment key={`key${doubtIndex}`}>
              {displayDoubt(doubt)}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const handleDateChanged = (event: React.SyntheticEvent, data) => {
    editorRef.editor.setValue(data.value, 1);
    editorRef.editor.focus();
  };

  return (
    <Segment className="searchBox">
      {!_.isNull(metadata) && (
        <React.Fragment>
          {currentDoubts && showDoubts && displayDoubts()}
          <Segment attached={true} style={{ border: "none", padding: "6px" }}>
            <Icon name="search" style={{ position: "relative", top: "-7px" }} />
            <AceEditor
              // placeholder="Ask your question..." TODO make this conditional
              placeholder="continue asking your question or type reset to start over"
              ref={setEditorRef}
              className="mousetrap"
              theme="textmate"
              mode={"text"}
              value={currentValue}
              editorProps={editorProps}
              minLines={1}
              width="100%"
              height="100%"
              setOptions={editorOptions}
              showPrintMargin={false}
              wrapEnabled={true}
              showGutter={false}
              highlightActiveLine={false}
              onLoad={initEditor}
              onChange={handleChange}
            />
          </Segment>
        </React.Fragment>
      )}
    </Segment>
  );
};

export default SearchBox;



// WEBPACK FOOTER //
// ./src/components/Query/SearchBox.tsx